import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@khan/data-gql";
import Log, {Errors} from "@khan/logging";

import {SIGNUP_RESEND_VERIFICATION_EMAIL_MUTATION} from "./verify-email-mutations.ts";

const strings = {
    resendEmailButton: (email?: string | null) =>
        email ? t`Resend email to ${email}` : t`Resend email`,
} as const;

interface Props {
    email: string | null | undefined;
}

type SendStatus = "unsent" | "sending" | "sent" | "failed";

export const ResendVerificationEmailButton: React.FC<Props> = ({email}) => {
    const [sendStatus, setSendStatus] = useState<SendStatus>("unsent");
    const [resendVerificationEmail] = useMutation(
        SIGNUP_RESEND_VERIFICATION_EMAIL_MUTATION,
    );

    const handleResendClick = useCallback(async () => {
        setSendStatus("sending");

        if (email) {
            try {
                const result = await resendVerificationEmail({});

                if (result.status === "success") {
                    setSendStatus("sent");
                } else {
                    setSendStatus("failed");
                }
            } catch (error) {
                setSendStatus("failed");
            }
        } else {
            // We don't expect to ever hit this case
            Log.error(
                "User attempted to resend verification email without an email",
                Errors.Internal,
            );
            setSendStatus("failed");
        }
    }, [email, resendVerificationEmail]);

    if (sendStatus === "unsent" || sendStatus === "sending") {
        return (
            <Button
                kind="tertiary"
                spinner={sendStatus === "sending"}
                onClick={handleResendClick}
            >
                {strings.resendEmailButton(email)}
            </Button>
        );
    }

    if (sendStatus === "sent") {
        return (
            <View style={styles.centeredContainer}>
                <Body style={styles.successText}>{t`Email sent!`}</Body>
            </View>
        );
    }

    return (
        <View style={styles.centeredContainer} aria-live="polite">
            <Body style={styles.errorText}>
                {t`Oops! We were unable to send you an email.`}
            </Body>
            <Button kind="tertiary" onClick={handleResendClick}>
                {t`Try again?`}
            </Button>
        </View>
    );
};

const styles = StyleSheet.create({
    centeredContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    errorText: {
        color: semanticColor.status.critical.foreground,
        marginBlockStart: sizing.size_040,
    },
    successText: {
        marginBlockStart: sizing.size_120,
        marginBlockEnd: sizing.size_120,
    },
});

export default ResendVerificationEmailButton;
